import { useState, useRef, useEffect } from "react";
import { usePageTitle } from "@/hooks/usePageTitle";
import { useNavigate, Link } from "react-router-dom";
import {
  User, Camera, BookOpen, Settings2,
  ArrowRight, ArrowLeft, CheckCircle, BookOpenCheck, ChevronDown, Search
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { SelectableTags } from "@/components/ui/selectable-tags";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SidebarProvider, SidebarInset } from "@/components/ui/sidebar";
import { DashboardTopNav } from "@/components/layout/DashboardTopNav";
import { StudentSidebar } from "@/components/student/dashboard/StudentSidebar";
import { subjects, grades, teachingStyles } from "@/lib/data";
import { FieldHint } from "@/components/help/FieldHint";
import { useGeoDefaults } from "@/hooks/useGeoDefaults";
import { useAuth } from "@/hooks/use-auth-compat";
import { currencySymbol as getCurrencySymbol } from "@/lib/currency";
import studentProfileHelp from "@/content/help/student-profile-help.json";

// ── Country dial-code data ──────────────────────────────────────────────────
interface Country { name: string; code: string; dial: string; flag: string }
const COUNTRIES: Country[] = [
  { name: "Afghanistan", code: "AF", dial: "+93", flag: "🇦🇫" },
  { name: "Albania", code: "AL", dial: "+355", flag: "🇦🇱" },
  { name: "Algeria", code: "DZ", dial: "+213", flag: "🇩🇿" },
  { name: "Argentina", code: "AR", dial: "+54", flag: "🇦🇷" },
  { name: "Australia", code: "AU", dial: "+61", flag: "🇦🇺" },
  { name: "Austria", code: "AT", dial: "+43", flag: "🇦🇹" },
  { name: "Bangladesh", code: "BD", dial: "+880", flag: "🇧🇩" },
  { name: "Belgium", code: "BE", dial: "+32", flag: "🇧🇪" },
  { name: "Brazil", code: "BR", dial: "+55", flag: "🇧🇷" },
  { name: "Canada", code: "CA", dial: "+1", flag: "🇨🇦" },
  { name: "Chile", code: "CL", dial: "+56", flag: "🇨🇱" },
  { name: "China", code: "CN", dial: "+86", flag: "🇨🇳" },
  { name: "Colombia", code: "CO", dial: "+57", flag: "🇨🇴" },
  { name: "Czech Republic", code: "CZ", dial: "+420", flag: "🇨🇿" },
  { name: "Denmark", code: "DK", dial: "+45", flag: "🇩🇰" },
  { name: "Egypt", code: "EG", dial: "+20", flag: "🇪🇬" },
  { name: "Ethiopia", code: "ET", dial: "+251", flag: "🇪🇹" },
  { name: "Finland", code: "FI", dial: "+358", flag: "🇫🇮" },
  { name: "France", code: "FR", dial: "+33", flag: "🇫🇷" },
  { name: "Germany", code: "DE", dial: "+49", flag: "🇩🇪" },
  { name: "Ghana", code: "GH", dial: "+233", flag: "🇬🇭" },
  { name: "Greece", code: "GR", dial: "+30", flag: "🇬🇷" },
  { name: "Hong Kong", code: "HK", dial: "+852", flag: "🇭🇰" },
  { name: "Hungary", code: "HU", dial: "+36", flag: "🇭🇺" },
  { name: "India", code: "IN", dial: "+91", flag: "🇮🇳" },
  { name: "Indonesia", code: "ID", dial: "+62", flag: "🇮🇩" },
  { name: "Iran", code: "IR", dial: "+98", flag: "🇮🇷" },
  { name: "Iraq", code: "IQ", dial: "+964", flag: "🇮🇶" },
  { name: "Ireland", code: "IE", dial: "+353", flag: "🇮🇪" },
  { name: "Israel", code: "IL", dial: "+972", flag: "🇮🇱" },
  { name: "Italy", code: "IT", dial: "+39", flag: "🇮🇹" },
  { name: "Japan", code: "JP", dial: "+81", flag: "🇯🇵" },
  { name: "Jordan", code: "JO", dial: "+962", flag: "🇯🇴" },
  { name: "Kenya", code: "KE", dial: "+254", flag: "🇰🇪" },
  { name: "Malaysia", code: "MY", dial: "+60", flag: "🇲🇾" },
  { name: "Mexico", code: "MX", dial: "+52", flag: "🇲🇽" },
  { name: "Morocco", code: "MA", dial: "+212", flag: "🇲🇦" },
  { name: "Myanmar", code: "MM", dial: "+95", flag: "🇲🇲" },
  { name: "Nepal", code: "NP", dial: "+977", flag: "🇳🇵" },
  { name: "Netherlands", code: "NL", dial: "+31", flag: "🇳🇱" },
  { name: "New Zealand", code: "NZ", dial: "+64", flag: "🇳🇿" },
  { name: "Nigeria", code: "NG", dial: "+234", flag: "🇳🇬" },
  { name: "Norway", code: "NO", dial: "+47", flag: "🇳🇴" },
  { name: "Pakistan", code: "PK", dial: "+92", flag: "🇵🇰" },
  { name: "Peru", code: "PE", dial: "+51", flag: "🇵🇪" },
  { name: "Philippines", code: "PH", dial: "+63", flag: "🇵🇭" },
  { name: "Poland", code: "PL", dial: "+48", flag: "🇵🇱" },
  { name: "Portugal", code: "PT", dial: "+351", flag: "🇵🇹" },
  { name: "Qatar", code: "QA", dial: "+974", flag: "🇶🇦" },
  { name: "Romania", code: "RO", dial: "+40", flag: "🇷🇴" },
  { name: "Russia", code: "RU", dial: "+7", flag: "🇷🇺" },
  { name: "Saudi Arabia", code: "SA", dial: "+966", flag: "🇸🇦" },
  { name: "Singapore", code: "SG", dial: "+65", flag: "🇸🇬" },
  { name: "South Africa", code: "ZA", dial: "+27", flag: "🇿🇦" },
  { name: "South Korea", code: "KR", dial: "+82", flag: "🇰🇷" },
  { name: "Spain", code: "ES", dial: "+34", flag: "🇪🇸" },
  { name: "Sri Lanka", code: "LK", dial: "+94", flag: "🇱🇰" },
  { name: "Sweden", code: "SE", dial: "+46", flag: "🇸🇪" },
  { name: "Switzerland", code: "CH", dial: "+41", flag: "🇨🇭" },
  { name: "Taiwan", code: "TW", dial: "+886", flag: "🇹🇼" },
  { name: "Tanzania", code: "TZ", dial: "+255", flag: "🇹🇿" },
  { name: "Thailand", code: "TH", dial: "+66", flag: "🇹🇭" },
  { name: "Turkey", code: "TR", dial: "+90", flag: "🇹🇷" },
  { name: "Uganda", code: "UG", dial: "+256", flag: "🇺🇬" },
  { name: "Ukraine", code: "UA", dial: "+380", flag: "🇺🇦" },
  { name: "United Arab Emirates", code: "AE", dial: "+971", flag: "🇦🇪" },
  { name: "United Kingdom", code: "GB", dial: "+44", flag: "🇬🇧" },
  { name: "United States", code: "US", dial: "+1", flag: "🇺🇸" },
  { name: "Venezuela", code: "VE", dial: "+58", flag: "🇻🇪" },
  { name: "Vietnam", code: "VN", dial: "+84", flag: "🇻🇳" },
];

// ── Country code autocomplete combobox ─────────────────────────────────────
function CountryCodeSelect({ value, onChange }: { value: Country; onChange: (c: Country) => void }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const filtered = query.trim()
    ? COUNTRIES.filter(c =>
        c.name.toLowerCase().includes(query.toLowerCase()) ||
        c.dial.includes(query)
      )
    : COUNTRIES;

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 0);
    else setQuery("");
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="h-11 flex items-center gap-1.5 px-3 rounded-lg border border-input bg-background hover:bg-accent transition-colors text-sm whitespace-nowrap focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <span className="text-base leading-none">{value.flag}</span>
        <span className="font-medium text-foreground">{value.dial}</span>
        <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />
      </button>

      {open && (
        <div className="absolute z-50 top-full left-0 mt-1 w-64 rounded-xl border border-border bg-popover shadow-lg overflow-hidden">
          <div className="p-2 border-b border-border">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
              <input
                ref={inputRef}
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder="Search country or code…"
                className="w-full pl-8 pr-3 py-1.5 text-sm bg-muted/50 rounded-lg outline-none placeholder:text-muted-foreground"
              />
            </div>
          </div>
          <ul className="max-h-52 overflow-y-auto py-1">
            {filtered.length === 0 ? (
              <li className="px-3 py-2 text-sm text-muted-foreground">No results</li>
            ) : filtered.map(c => (
              <li key={c.code}>
                <button
                  type="button"
                  onClick={() => { onChange(c); setOpen(false); }}
                  className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm hover:bg-accent transition-colors text-left ${c.code === value.code ? "bg-accent font-medium" : ""}`}
                >
                  <span className="text-base leading-none shrink-0">{c.flag}</span>
                  <span className="flex-1 truncate">{c.name}</span>
                  <span className="text-muted-foreground shrink-0">{c.dial}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

const steps = [
  { id: 1, title: "About", icon: User },
  { id: 2, title: "Photo", icon: Camera },
  { id: 3, title: "Subjects", icon: BookOpen },
  { id: 4, title: "Preferences", icon: Settings2 },
];

/** Keys into student-profile-help.json, indexed by step id - 1. */
const HELP_STEP_IDS = ["about", "photo", "subjects", "preferences"];

export default function StudentProfileSetup() {
  usePageTitle('Profile Setup | BookMyCoaching');
  const [currentStep, setCurrentStep] = useState(1);
  const navigate = useNavigate();

  const { user } = useAuth();
  const { data: geoDefaults } = useGeoDefaults(user?.access_token);

  // SCRUM-365: backend currency only — no literal currency fallback (missing = no symbol).
  const currency = geoDefaults?.currency ?? '';
  const currencySymbol = getCurrencySymbol(currency);
  const geoMin = geoDefaults?.minPrice ?? 500;
  const geoMax = geoDefaults?.maxPrice ?? 2000;

  const [countryCode, setCountryCode] = useState<Country>(
    COUNTRIES.find(c => c.code === "IN") ?? COUNTRIES[0]
  );

  const [budgetTouched, setBudgetTouched] = useState(false);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    phoneNumber: "",
    grade: "",
    selectedSubjects: [] as string[],
    goals: "",
    budgetMin: 0,
    budgetMax: 0,
    preferredStyle: "",
    preferOnline: true,
    preferOffline: false,
  });

  // Sync geo-aware budget defaults once loaded, unless the user has already edited the fields.
  useEffect(() => {
    if (!budgetTouched && geoDefaults) {
      setFormData(prev => ({ ...prev, budgetMin: geoDefaults.minPrice, budgetMax: geoDefaults.maxPrice }));
    }
  }, [geoDefaults, budgetTouched]);

  // Display values: fall back to geo defaults until geoDefaults loads and syncs.
  const displayBudgetMin = budgetTouched ? formData.budgetMin : (geoDefaults?.minPrice ?? geoMin);
  const displayBudgetMax = budgetTouched ? formData.budgetMax : (geoDefaults?.maxPrice ?? geoMax);

  const progress = Math.round((currentStep / steps.length) * 100);

  const handleFinish = () => {
    navigate("/student-dashboard");
  };

  return (
    <SidebarProvider>
      <DashboardTopNav role="student" />
      <div className="min-h-screen flex w-full student-theme pt-14">
        <StudentSidebar
          studentName="Alex Johnson"
          studentEmail="alex.johnson@email.com"
          studentAvatar="https://images.unsplash.com/photo-1527980965255-d3b416303d12?w=200"
        />
        <SidebarInset className="flex flex-col">

          {/* Main content */}
          <main className="flex-1 overflow-y-auto">
            {/* Progress bar */}
            <div className="bg-background border-b border-border/50">
              <div className="max-w-3xl mx-auto px-4 sm:px-6 py-3">
                <div className="flex items-center justify-between mb-2">
                  <h2 className="text-base font-medium text-foreground">
                    Step {currentStep} of {steps.length}: {steps[currentStep - 1].title}
                  </h2>
                </div>
                <Progress value={progress} className="h-1.5" />
                <div className="flex justify-between mt-2">
                  {steps.map((step) => {
                    const Icon = step.icon;
                    const isActive = currentStep === step.id;
                    const isDone = currentStep > step.id;
                    return (
                      <button
                        key={step.id}
                        onClick={() => step.id <= currentStep && setCurrentStep(step.id)}
                        className={`flex flex-col items-center gap-0.5 transition-all ${
                          isActive ? "text-primary" : isDone ? "text-primary/60" : "text-muted-foreground/40"
                        } ${step.id <= currentStep ? "cursor-pointer" : "cursor-default"}`}
                      >
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                          isActive ? "bg-primary text-primary-foreground" :
                          isDone ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"
                        }`}>
                          {isDone ? <CheckCircle className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
                        </div>
                        <span className="text-xs font-medium hidden sm:block">{step.title}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="max-w-3xl xl:max-w-5xl mx-auto px-4 sm:px-6 py-8 flex items-start gap-6">
              <div className="flex-1 min-w-0 bg-card rounded-2xl border border-border p-6 sm:p-8 shadow-sm">
                {/* Step 1: About */}
                {currentStep === 1 && (
                  <div className="space-y-5">
                    <div>
                      <h3 className="font-display text-2xl text-foreground mb-1">Basic Information</h3>
                      <p className="text-muted-foreground">Tell us about yourself</p>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>First Name</Label>
                        <Input value={formData.firstName} onChange={e => setFormData({...formData, firstName: e.target.value})} placeholder="First name" className="h-11" />
                      </div>
                      <div className="space-y-2">
                        <Label>Last Name</Label>
                        <Input value={formData.lastName} onChange={e => setFormData({...formData, lastName: e.target.value})} placeholder="Last name" className="h-11" />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Phone Number (optional) <FieldHint content={studentProfileHelp} stepId="about" fieldKey="phoneNumber" /></Label>
                      <div className="flex gap-2">
                        <CountryCodeSelect value={countryCode} onChange={setCountryCode} />
                        <Input
                          value={formData.phoneNumber}
                          onChange={e => setFormData({ ...formData, phoneNumber: e.target.value.replace(/[^\d\s\-().]/g, "") })}
                          placeholder="Phone number"
                          className="h-11 flex-1"
                          type="tel"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Grade Level <FieldHint content={studentProfileHelp} stepId="about" fieldKey="grade" /></Label>
                      <SelectableTags
                        options={grades}
                        selected={formData.grade ? [formData.grade] : []}
                        onToggle={(g) => setFormData({...formData, grade: formData.grade === g ? "" : g})}
                        allowCustom multiSelect={false}
                        placeholder="Enter custom grade..."
                      />
                    </div>
                  </div>
                )}

                {/* Step 2: Photo */}
                {currentStep === 2 && (
                  <div className="space-y-5">
                    <div>
                      <p className="text-muted-foreground">Add a photo so teachers can recognize you</p>
                    </div>
                    <div className="border-2 border-dashed border-border rounded-2xl p-10 text-center hover:border-primary/50 hover:bg-accent/30 transition-all cursor-pointer">
                      <div className="w-20 h-20 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
                        <Camera className="w-8 h-8 text-muted-foreground" />
                      </div>
                       <p className="font-medium text-foreground mb-1">Click to upload your photo</p>
                       <p className="text-sm text-muted-foreground">JPG, PNG up to 5MB</p>
                    </div>
                  </div>
                )}

                {/* Step 3: Subjects */}
                {currentStep === 3 && (
                  <div className="space-y-5">
                    <div>
                       <h3 className="font-display text-2xl text-foreground mb-1">Subjects & Goals</h3>
                       <p className="text-muted-foreground">What do you need help with?</p>
                    </div>
                    <div className="space-y-2">
                      <Label>Subjects <FieldHint content={studentProfileHelp} stepId="subjects" fieldKey="subjects" /></Label>
                      <SelectableTags
                        options={subjects}
                        selected={formData.selectedSubjects}
                        onToggle={(s) => setFormData(prev => ({ ...prev, selectedSubjects: prev.selectedSubjects.includes(s) ? prev.selectedSubjects.filter(x => x !== s) : [...prev.selectedSubjects, s] }))}
                        allowCustom placeholder="Add custom subject..."
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Learning Goals (optional) <FieldHint content={studentProfileHelp} stepId="subjects" fieldKey="goals" /></Label>
                      <Textarea
                        value={formData.goals}
                        onChange={e => setFormData({...formData, goals: e.target.value})}
                        placeholder="What do you want to achieve? e.g. pass my exams, get better at maths..."
                        rows={3}
                        className="resize-none"
                      />
                    </div>
                  </div>
                )}

                {/* Step 4: Preferences */}
                {currentStep === 4 && (
                  <div className="space-y-5">
                    <div>
                      <h3 className="font-display text-2xl text-foreground mb-1">Preferences</h3>
                      <p className="text-muted-foreground">Help us match you with the right teacher</p>
                    </div>
                    <div className="space-y-2">
                      <Label>Budget Range (per session) <FieldHint content={studentProfileHelp} stepId="preferences" fieldKey="budget" /></Label>
                      <div className="flex items-center gap-3">
                        <div className="relative flex-1">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">{currencySymbol}</span>
                          <Input type="number" value={displayBudgetMin} onChange={e => { setBudgetTouched(true); setFormData({...formData, budgetMin: Number(e.target.value)}); }} className="pl-9 h-11" min={0} />
                        </div>
                        <span className="text-muted-foreground text-sm">to</span>
                        <div className="relative flex-1">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">{currencySymbol}</span>
                          <Input type="number" value={displayBudgetMax} onChange={e => { setBudgetTouched(true); setFormData({...formData, budgetMax: Number(e.target.value)}); }} className="pl-9 h-11" min={0} />
                        </div>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Preferred Teaching Style <FieldHint content={studentProfileHelp} stepId="preferences" fieldKey="preferredStyle" /></Label>
                      <Select value={formData.preferredStyle} onValueChange={v => setFormData({...formData, preferredStyle: v})}>
                        <SelectTrigger className="h-11"><SelectValue placeholder="Select style (optional)" /></SelectTrigger>
                        <SelectContent>
                          {teachingStyles.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Lesson Format <FieldHint content={studentProfileHelp} stepId="preferences" fieldKey="lessonFormat" /></Label>
                      <div className="grid grid-cols-2 gap-3">
                        <label className="flex items-center gap-3 p-3 rounded-lg border border-border hover:bg-accent transition-colors cursor-pointer">
                          <Checkbox checked={formData.preferOnline} onCheckedChange={c => setFormData({...formData, preferOnline: c as boolean})} />
                          <span className="text-sm text-foreground">Online</span>
                        </label>
                        <label className="flex items-center gap-3 p-3 rounded-lg border border-border hover:bg-accent transition-colors cursor-pointer">
                          <Checkbox checked={formData.preferOffline} onCheckedChange={c => setFormData({...formData, preferOffline: c as boolean})} />
                          <span className="text-sm text-foreground">Offline</span>
                        </label>
                      </div>
                    </div>
                  </div>
                )}

                {/* Navigation */}
                <div className="flex gap-3 mt-8 pt-6 border-t border-border">
                  {currentStep > 1 && (
                    <Button variant="outline" onClick={() => setCurrentStep(currentStep - 1)} className="gap-2">
                      <ArrowLeft className="w-4 h-4" /> Back
                    </Button>
                  )}
                  <div className="flex-1" />
                  {currentStep < steps.length ? (
                    <Button onClick={() => setCurrentStep(currentStep + 1)} className="gap-2">
                      Continue <ArrowRight className="w-4 h-4" />
                    </Button>
                  ) : (
                    <Button onClick={handleFinish} className="gap-2">
                      Finish Setup <CheckCircle className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>

            </div>
          </main>
        </SidebarInset>
      </div>
    </SidebarProvider>
  );
}
